import { useEffect, useRef } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/types/auth';
import { getDeviceId } from '@/utils/device';
import logger from '@/utils/logger';
import { resetLocalCache } from '@/services/sync/resetLocalCache';
import { networkService } from '@/services/network.service';
import { ensureValidSession, isJwtExpiredError } from '@/services/auth/sessionCache';

interface SyncCommandRow {
  id: string;
  user_id: string;
  device_id: string;
  command: string;
  payload: Record<string, unknown> | null;
  created_at: string;
  processed_at: string | null;
}

type CommandQueue = Promise<void>;

const POLLING_INTERVAL_MS = 60_000;

export function useSyncCommandsWatcher(user: User | null): void {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const queueRef = useRef<CommandQueue>(Promise.resolve());
  const processingIdsRef = useRef<Set<string>>(new Set());
  const processedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!user) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      processingIdsRef.current.clear();
      processedIdsRef.current.clear();
      queueRef.current = Promise.resolve();
      return;
    }

    const deviceId = getDeviceId();

    const executeCommand = async (command: SyncCommandRow) => {
      let processed = false;
      try {
        // Refresh the token before touching Supabase so a command executed after
        // a long background suspension doesn't fail with an expired JWT.
        await ensureValidSession();

        if (command.command === 'reset_cache') {
          const isCurrentlyOnline = networkService.isOnline();

          const { reloadedData, initialSyncError, initialSyncStatus } = await resetLocalCache({
            userId: user.id,
            isOnline: isCurrentlyOnline,
            loggerContext: 'SyncCommandsWatcher',
            triggerType: 'remote-command',
            onCacheCleared: () => {
              logger.info('[SyncCommandsWatcher] Cache locale svuotata', {
                commandId: command.id,
                userId: user.id,
                deviceId,
              });
            },
          });

          if (!reloadedData && isCurrentlyOnline) {
            logger.warn('[SyncCommandsWatcher] Sincronizzazione iniziale non completata', {
              commandId: command.id,
              userId: user.id,
              deviceId,
              initialSyncStatus,
              initialSyncError,
            });
          }

          const { error } = await supabase
            .from('sync_commands')
            .update({ processed_at: new Date().toISOString() })
            .eq('id', command.id)
            .is('processed_at', null);

          if (error) {
            logger.logError('SyncCommandsWatcher', 'Errore nel marcare il comando come elaborato', {
              additionalInfo: error,
            });
          } else {
            logger.info('[SyncCommandsWatcher] Comando reset_cache completato', {
              commandId: command.id,
              userId: user.id,
              deviceId,
            });
            processed = true;
          }
        } else {
          logger.warn('[SyncCommandsWatcher] Comando non gestito', {
            command: command.command,
            commandId: command.id,
          });

          const { error } = await supabase
            .from('sync_commands')
            .update({ processed_at: new Date().toISOString() })
            .eq('id', command.id)
            .is('processed_at', null);

          if (error) {
            logger.logError('SyncCommandsWatcher', 'Errore nel marcare come gestito un comando sconosciuto', {
              additionalInfo: error,
            });
          } else {
            processed = true;
          }
        }
      } catch (error) {
        logger.logError('SyncCommandsWatcher', 'Errore durante l\'esecuzione del comando remoto', {
          additionalInfo: error,
        });
      }

      return processed;
    };

    const enqueueCommand = (command: SyncCommandRow) => {
      if (processingIdsRef.current.has(command.id) || processedIdsRef.current.has(command.id)) {
        return;
      }

      processingIdsRef.current.add(command.id);

      queueRef.current = queueRef.current
        .then(async () => {
          const processed = await executeCommand(command);
          if (processed) {
            processedIdsRef.current.add(command.id);
          }
        })
        .catch((error) => {
          logger.logError('SyncCommandsWatcher', 'Errore durante la coda dei comandi', {
            additionalInfo: error,
          });
        })
        .finally(() => {
          processingIdsRef.current.delete(command.id);
        });
    };

    const queryPendingCommands = () =>
      supabase
        .from('sync_commands')
        .select('*')
        .eq('user_id', user.id)
        .eq('device_id', deviceId)
        .is('processed_at', null)
        .order('created_at', { ascending: true });

    const fetchPendingCommands = async () => {
      if (!networkService.isOnline()) {
        return;
      }

      // Proactively refresh the token so the poll right after app resume doesn't
      // fire with a stale JWT and cascade into generic "global" errors.
      await ensureValidSession();

      let { data, error } = await queryPendingCommands();

      // A still-expired JWT here means auto-refresh hadn't caught up: force a
      // refresh and retry once before giving up.
      if (error && isJwtExpiredError(error)) {
        const refreshed = await ensureValidSession();
        if (refreshed) {
          ({ data, error } = await queryPendingCommands());
        }
      }

      if (error) {
        if (isJwtExpiredError(error)) {
          logger.logError('SyncCommandsWatcher', 'Sessione scaduta: refresh non riuscito', {
            errorCode: 'PGRST303',
            error,
          });
        } else {
          logger.logError('SyncCommandsWatcher', 'Errore nel recupero dei comandi pendenti', {
            error,
          });
        }
        return;
      }

      (data ?? []).forEach((command) => {
        enqueueCommand(command as SyncCommandRow);
      });
    };

    void fetchPendingCommands();

    intervalRef.current = window.setInterval(() => {
      void fetchPendingCommands();
    }, POLLING_INTERVAL_MS);

    const channel = supabase
      .channel(`sync-commands-${user.id}-${deviceId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'sync_commands',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const command = payload.new as SyncCommandRow | undefined;
          if (!command || command.device_id !== deviceId || command.processed_at) {
            return;
          }
          enqueueCommand(command);
        }
      )
      .subscribe((status) => {
        logger.debug('[SyncCommandsWatcher] Stato canale realtime', {
          status,
          userId: user.id,
          deviceId,
        });
      });

    channelRef.current = channel;

    // When the app returns to foreground, refresh the session and check for
    // pending commands immediately instead of waiting up to a full poll cycle.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void fetchPendingCommands();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      processingIdsRef.current.clear();
      processedIdsRef.current.clear();
      queueRef.current = Promise.resolve();
    };
  }, [user?.id]);
}
